import * as React from 'react';

import type { W_BillingCode_FindForJob_VM_Single } from 'ab-api/web/billingCode/findForJob';

import type { BillingCodeVM } from 'ab-viewModels/billingCode.viewModel';
import type WorkSummaryVM from 'ab-viewModels/fieldReport/workSummary.viewModel';
import type WorkSummaryDetailVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';
import type SiblingSubjobVM from 'ab-viewModels/workRequest/siblingSubjob.viewModel';

import { filterMap } from '@acceligentllc/shared/utils/array';

import type { CellWidthsMap } from 'af-utils/react.util';
import * as SettingsUtils from 'af-utils/settings.util';

import BrowserStorageEnum from 'ab-enums/browserStorage.enum';
import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import * as SettingsKeys from 'af-constants/settingsKeys';

import AssignAllToSubJobModal from './AssignAllToSubJobModal';
import type { BillableWorkTableRowProps, NonBillableWorkTableRowProps, MaterialCostTableRowProps, NonBillableMaterialCostRowProps } from './BillableAndNonBillableWorkTableRow';
import WorkSummaryBillableWorkTableRow from './BillableAndNonBillableWorkTableRow';
import BulkEditBillableStatusModal from './BulkEditBillableStatusModal';
import { resolveHighlightGroupKey } from '../../helpers';
import WorkSummaryTable from '../../Table/index';
import { WorkSummaryTableSortKey, WORK_SUMMARY_TABLE_SORT_META } from '../../Table/values';
import type { WorkSummaryColumnMeta } from '../../values';
import { WORK_SUMMARY_BILLABLE_WORK_TABLE_COLUMNS, WORK_SUMMARY_MATERIAL_COST_TABLE_COLUMNS, WORK_SUMMARY_MATERIAL_NON_COST_WORK_TABLE_COLUMNS, WORK_SUMMARY_NON_BILLABLE_WORK_TABLE_COLUMNS, WorkSummaryColumns, replicateColumnArray } from '../../values';

interface BillableWorkSummaryProps {
	isBillable: true;
	billingType: WorkSummaryBillingType.BILLABLE_WORK;
	onBillingCodeSelect: (billingCode: Nullable<BillingCodeVM>, workSummaryDetailIds: number[]) => Promise<void>;
}
interface BillableMaterialCostsProps {
	isBillable: true;
	billingType: WorkSummaryBillingType.MATERIAL_COST;
	onBillingCodeSelect?: never;
}

interface NonBillableWorkSummaryProps {
	isBillable: false;
	billingType: WorkSummaryBillingType.BILLABLE_WORK;
	onBillingCodeSelect?: never;
}

interface NonBillableMaterialCostsProps {
	isBillable: false;
	billingType: WorkSummaryBillingType.MATERIAL_COST;
	onBillingCodeSelect?: never;
}

interface OwnPropsBase {
	workSummaryDetails: WorkSummaryDetailVM[];
	workSummary: WorkSummaryVM[];
	billingCodes: Record<string, W_BillingCode_FindForJob_VM_Single[]>;
	isBillableStatusEditable: boolean;
	isReadOnly: boolean;
	onRowHighlight: (groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => void;
	highlightedGroup: Nullable<string>;
	highlightedGroupKey: Nullable<string>;
	workSummaryDetailsByGroupKey: Record<string, WorkSummaryDetailVM[]>;
	alternativeWorkSummary: Set<WorkSummaryVM>;
	belongsToProject: boolean;
	handleJobReallocation: (subJob: SiblingSubjobVM, workSummaryDetailIds: number[]) => Promise<void>;
	onReallocateAll: (billingType: WorkSummaryBillingType, subJob: SiblingSubjobVM) => void;
	onBulkEditDetailBillableStatus: (workSummaryDetailIds: number[]) => void;
	subJobs: SiblingSubjobVM[];
	fieldReportId: number;
	workOrderId: number;
	billingType: WorkSummaryBillingType;
}

type Props = OwnPropsBase & (BillableWorkSummaryProps | NonBillableWorkSummaryProps | BillableMaterialCostsProps | NonBillableMaterialCostsProps);

const _maxDefinitionFieldsReducer = (_max: number, _currentWSD: { definitionFields: unknown[]; }) => {
	if (_currentWSD.definitionFields?.length > _max) {
		_max = _currentWSD.definitionFields?.length;
	}

	return _max;
};

const _resolveWorkSummaryBillableWorkTableHeaders = (
	maxNumberOfDefinitionFields: number,
	isBillable: boolean,
	billingType: WorkSummaryBillingType,
	belongsToProject: boolean
) => {
	const headers: WorkSummaryColumnMeta[] = [];
	let headerTemplates: WorkSummaryColumnMeta[] | undefined = undefined;
	if (isBillable) {
		if (billingType === WorkSummaryBillingType.BILLABLE_WORK) {
			headerTemplates = WORK_SUMMARY_BILLABLE_WORK_TABLE_COLUMNS;
		} else if (billingType === WorkSummaryBillingType.MATERIAL_COST) {
			headerTemplates = WORK_SUMMARY_MATERIAL_COST_TABLE_COLUMNS;
		}
	} else {
		if (billingType === WorkSummaryBillingType.BILLABLE_WORK) {
			headerTemplates = WORK_SUMMARY_NON_BILLABLE_WORK_TABLE_COLUMNS;
		} else if (billingType === WorkSummaryBillingType.MATERIAL_COST) {
			headerTemplates = WORK_SUMMARY_MATERIAL_NON_COST_WORK_TABLE_COLUMNS;
		}
	}
	if (!headerTemplates) {
		throw new Error('Unresolved Header Template');
	}
	for (const column of headerTemplates) {
		if (column.id === WorkSummaryColumns.DEFINITION_FIELD) {
			if (maxNumberOfDefinitionFields > 0) {
				headers.push(...replicateColumnArray(column, maxNumberOfDefinitionFields));
			}
		} else if (column.id === WorkSummaryColumns.REALLOCATE) {
			if (belongsToProject) {
				headers.push(column);
			}
		} else {
			headers.push(column);
		}
	}

	return headers;
};

const _findFirstWithGroupName = ({ workSummaryGroup }) => !!workSummaryGroup;

const _getGroupKey = (item: WorkSummaryVM) => resolveHighlightGroupKey({
	billingType: item.billingType,
	billableWorkId: item.billableWorkId,
	workType: item.type,
	definitionFields: item.definitionFields,
	subJobId: item.subJobId,
});

function _workSummariesAreEqual(
	detail: WorkSummaryDetailVM,
	summary: WorkSummaryVM
): boolean {
	// Check basic properties
	if (
		detail.billingType !== summary.billingType
		|| detail.billableWorkId !== summary.billableWorkId
		|| detail.subJobId !== summary.subJobId
		|| detail.billingCodeId !== summary.billingCodeId
		|| detail.materialId !== summary.materialId
		|| detail.materialCostSource !== summary.materialCostSource
		|| detail.isBillable !== summary.isBillable
		|| detail.workName !== summary.work
		|| detail.workType !== summary.type
		|| detail.workSummaryGroup !== summary.workSummaryGroup
	) {
		return false;
	}

	// Check definition fields
	if (detail.definitionFields.length !== summary.definitionFields.length) {
		return false;
	}

	for (let i = 0; i < detail.definitionFields.length; i++) {
		const detailField = detail.definitionFields[i];
		const summaryField = summary.definitionFields[i];

		if (detailField.fieldType !== summaryField.fieldType ||
			detailField.name !== summaryField.fieldName ||
			detailField.value !== summaryField.value) {
			return false;
		}
	}

	return true;
}

const _shouldShowWorkSummaryDetailsWarning = () => {
	const hasBeenShown = SettingsUtils.getItem(SettingsKeys.WORK_SUMMARY_DETAILS_BULK_EDIT_STATUS_WARNING(), BrowserStorageEnum.LOCAL_STORAGE);
	return hasBeenShown !== 'true';
};

const _setShowWorkSummaryDetailsWarning = (showModalAgain: boolean) => {
	SettingsUtils.setItem(SettingsKeys.WORK_SUMMARY_DETAILS_BULK_EDIT_STATUS_WARNING(), showModalAgain.toString(), BrowserStorageEnum.LOCAL_STORAGE);
};

const BillableAndNonBillableWorkTable: React.FC<Props> = (props) => {
	const {
		workSummaryDetails,
		workSummary,
		billingCodes,
		isBillable,
		onBillingCodeSelect,
		onRowHighlight,
		highlightedGroup,
		isBillableStatusEditable,
		isReadOnly,
		highlightedGroupKey,
		workSummaryDetailsByGroupKey,
		alternativeWorkSummary,
		belongsToProject,
		subJobs,
		onReallocateAll,
		onBulkEditDetailBillableStatus,
		handleJobReallocation,
		fieldReportId,
		workOrderId,
		billingType,
	} = props;

	const [showAssignAllModal, setShowAssignAllModal] = React.useState(false);
	const [showBulkEditBillableStatusModal, setShowBulkEditBillableStatusModal] = React.useState(false);
	const [workSummaryDetailIds, setWorkSummaryDetailIds] = React.useState<number[]>([]);

	const closeAssignAllModal = React.useCallback(() => setShowAssignAllModal(false), []);
	const closeBulkEditBillableStatusModal = React.useCallback(() => {
		setShowBulkEditBillableStatusModal(false);
		setWorkSummaryDetailIds([]);
	}, [setShowBulkEditBillableStatusModal]);

	const openAssignAllModal = React.useCallback(() => setShowAssignAllModal(true), []);
	const openBulkEditBillableStatusModal = React.useCallback((_workSummaryDetailIds: number[]) => {
		if (_shouldShowWorkSummaryDetailsWarning()) {
			setShowBulkEditBillableStatusModal(true);
			setWorkSummaryDetailIds(_workSummaryDetailIds);
		} else {
			onBulkEditDetailBillableStatus(_workSummaryDetailIds);
		}
	}, [onBulkEditDetailBillableStatus]);

	const handleOnBulkEditBillableStatus = React.useCallback((showModalAgain: boolean) => {
		_setShowWorkSummaryDetailsWarning(showModalAgain);
		onBulkEditDetailBillableStatus(workSummaryDetailIds);
	}, [onBulkEditDetailBillableStatus, workSummaryDetailIds]);

	const maxNumberOfDefinitionFieldsFromDetails = workSummaryDetails?.reduce(_maxDefinitionFieldsReducer, 0) ?? 0;
	const maxNumberOfDefinitionFieldsFromSummary = workSummary?.reduce(_maxDefinitionFieldsReducer, 0) ?? 0;
	const maxNumberOfDefinitionFields = Math.max(maxNumberOfDefinitionFieldsFromDetails, maxNumberOfDefinitionFieldsFromSummary);

	const tableColumns = React.useMemo(
		() => {
			const columns = _resolveWorkSummaryBillableWorkTableHeaders(maxNumberOfDefinitionFields, isBillable, billingType, belongsToProject ?? false);
			return columns.map((_header) => {
				if (_header.id === WorkSummaryColumns.REALLOCATE && !!workSummary.length && !isReadOnly) {
					return { ..._header, additionalColumnAction: { text: 'Assign All', icon: 'edit', action: openAssignAllModal } };
				}
				return _header;
			});
		},
		[belongsToProject, billingType, isBillable, isReadOnly, maxNumberOfDefinitionFields, openAssignAllModal, workSummary.length]
	);

	const renderWorkSummaryBillableWorkTableRow = React.useCallback((data: WorkSummaryVM, cellWidths: CellWidthsMap) => {
		let billableDependentProps: (
			BillableWorkTableRowProps | NonBillableWorkTableRowProps | MaterialCostTableRowProps | NonBillableMaterialCostRowProps | undefined
		) = undefined;
		if (isBillable) {
			if (billingType === WorkSummaryBillingType.BILLABLE_WORK) {
				billableDependentProps = { isBillable, billingType, onBillingCodeSelect };
			} else if (billingType === WorkSummaryBillingType.MATERIAL_COST) {
				billableDependentProps = { isBillable, billingType };
			}
		} else {
			billableDependentProps = { isBillable, billingType };
		}

		if (!billableDependentProps) {
			throw new Error('Failed to resolve billable dependent props');
		}

		const groupKey = resolveHighlightGroupKey({
			billingType: data.billingType,
			billableWorkId: data.billableWorkId,
			workType: data.type,
			definitionFields: data.definitionFields,
			subJobId: data.subJobId,
		});
		const hasPreviouslyBeenSplit = !!workSummaryDetailsByGroupKey[groupKey]?.find(_findFirstWithGroupName);
		const isOfCurrentlyHighlightedGroupKey = highlightedGroupKey === groupKey;
		const isAlternativeOption = alternativeWorkSummary.has(data);

		const ids = filterMap(
			workSummaryDetails,
			(_wsd) => _workSummariesAreEqual(_wsd, data),
			(_wsd) => _wsd.id
		);

		const subJobBillingCodes = billingCodes[data.subJobId] ?? [];

		return (
			<WorkSummaryBillableWorkTableRow
				belongsToProject={belongsToProject}
				billingCodes={subJobBillingCodes}
				cellWidths={cellWidths}
				fieldReportId={fieldReportId}
				handleBulkBillingStatusToggle={openBulkEditBillableStatusModal}
				handleJobReallocation={handleJobReallocation}
				hasPreviouslyBeenSplit={hasPreviouslyBeenSplit}
				highlightedGroup={highlightedGroup}
				isAlternativeOption={isAlternativeOption}
				isBillableStatusEditable={isBillableStatusEditable}
				isOfCurrentlyHighlightedGroupKey={isOfCurrentlyHighlightedGroupKey}
				isReadOnly={isReadOnly}
				key={data.workSummaryGroup ?? groupKey}
				maxNumberOfDefinitionFields={maxNumberOfDefinitionFields}
				onRowHighlight={onRowHighlight}
				rowData={data}
				subJobs={subJobs}
				workOrderId={workOrderId}
				workSummaryDetailIds={ids}
				{...billableDependentProps}
			/>
		);
	}, [
		isBillable,
		onBillingCodeSelect,
		belongsToProject,
		workSummaryDetailsByGroupKey,
		highlightedGroupKey,
		alternativeWorkSummary,
		workSummaryDetails,
		billingCodes,
		highlightedGroup,
		isBillableStatusEditable,
		isReadOnly,
		maxNumberOfDefinitionFields,
		onRowHighlight,
		subJobs,
		handleJobReallocation,
		fieldReportId,
		workOrderId,
		openBulkEditBillableStatusModal,
		billingType,
	]
	);

	const handleReallocateAllSubmit = React.useCallback((subJob: SiblingSubjobVM) => {
		onReallocateAll(billingType, subJob);
	}, [billingType, onReallocateAll]);

	const sort = WORK_SUMMARY_TABLE_SORT_META[WorkSummaryTableSortKey.TYPE].sort;

	return (
		<>
			<WorkSummaryTable<WorkSummaryVM>
				data={workSummary}
				getGroupKey={_getGroupKey}
				headers={tableColumns}
				highlightedGroupKey={highlightedGroupKey}
				renderRow={renderWorkSummaryBillableWorkTableRow}
				sort={sort}
				useGroups={true}
			/>
			<AssignAllToSubJobModal
				closeModal={closeAssignAllModal}
				onSubmit={handleReallocateAllSubmit}
				showModal={showAssignAllModal}
				subJobs={subJobs}
			/>
			<BulkEditBillableStatusModal
				billingType={billingType}
				closeModal={closeBulkEditBillableStatusModal}
				onSubmit={handleOnBulkEditBillableStatus}
				showModal={showBulkEditBillableStatusModal}
			/>
		</>
	);
};

export default React.memo(BillableAndNonBillableWorkTable);
